"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

/**
 * Reports how much of the fixed header's height is currently covered by a
 * `[data-header-invert]`-marked element, as a 0-1 fraction: 0 means no
 * marked section is behind the header at all, 1 means the header's entire
 * height is over one, and values in between happen while a marked section's
 * edge is scrolling through the header band. Header content (e.g. the Login
 * button) can use this to fade its color between a value that reads on dark
 * backgrounds and one that reads on light ones, tracking the scroll
 * position continuously instead of snapping between two fixed colors. Mark
 * a section with `data-header-invert` when its background is solid light —
 * gradients and video are ambiguous and deliberately left unmarked.
 *
 * `leadDistance` eases the entry from the gradient into the light section.
 * Exit tracks the actual header band: never fade toward white while the
 * full header is still on white. Adjacent/overlapping marked elements are
 * merged so their shared edges cannot restart the fade.
 *
 * The header lives in a layout shared across every public route and never
 * remounts on client-side (`next/link`) navigation, so this deliberately
 * re-queries the DOM fresh on every check instead of caching the matched
 * elements once: a cached reference would either (a) permanently miss
 * sections on routes that had none at mount, or (b) go stale after
 * navigating away from a route that did — a detached node's
 * getBoundingClientRect() is an all-zero rect, which would read as full
 * coverage unconditionally and get the color stuck wrong everywhere else.
 * `usePathname()` also re-runs `check()` right after navigation, rather
 * than waiting for the next scroll/resize on the new page.
 */
export default function useLightSectionBehindHeader(
  headerHeight = 75,
  leadDistance = 200
) {
  const [progress, setProgress] = useState(0);
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const span = headerHeight + leadDistance;

    // A handful of elements at most, queried fresh — cheap enough to run
    // directly on every scroll/resize tick with no throttling needed.
    const check = () => {
      const sections = document.querySelectorAll<HTMLElement>(
        "[data-header-invert]"
      );
      const spans = Array.from(sections, (el) => el.getBoundingClientRect())
        .filter((rect) => rect.bottom > rect.top)
        .sort((a, b) => a.top - b.top);
      const merged: Array<{ top: number; bottom: number }> = [];
      for (const rect of spans) {
        const previous = merged[merged.length - 1];
        if (previous && rect.top <= previous.bottom) {
          previous.bottom = Math.max(previous.bottom, rect.bottom);
        } else {
          merged.push({ top: rect.top, bottom: rect.bottom });
        }
      }

      let maxCoverage = 0;
      for (const rect of merged) {
        const entryFactor = Math.min(1, Math.max(0, (span - rect.top) / span));
        const exitFactor = Math.min(1, Math.max(0, rect.bottom / headerHeight));
        maxCoverage = Math.max(maxCoverage, Math.min(entryFactor, exitFactor));
      }
      setProgress(maxCoverage);
    };

    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [headerHeight, leadDistance, pathname]);

  return progress;
}
